import React, { useEffect, useRef, useState, type FC } from 'react';
import { Navigate, useLocation } from 'react-router';
// DL Fork: embedded-build session handshake + mobile body class support.
import EmbeddedReconnectingGate from '../ee/features/embed/EmbeddedReconnectingGate';
import {
    IS_EMBEDDED_BUILD,
    notifyParentReady,
    notifyParentSessionExpired,
} from '../ee/features/embed/parentMessaging';
import { useIsDlMobile } from '../hooks/useDlMobile';
import { usePromotePendingSsoLogin } from '../features/users/hooks/usePromotePendingSsoLogin';
import { useEmailStatus } from '../hooks/useEmailVerification';
import { useAccount } from '../hooks/user/useAccount';
import { useAbilityContext } from '../providers/Ability/useAbilityContext';
import useApp from '../providers/App/useApp';
import PageSpinner from './PageSpinner';

const PrivateRoute: FC<React.PropsWithChildren> = ({ children }) => {
    const {
        health,
        user: { data, isInitialLoading, isError },
    } = useApp();
    const location = useLocation();
    const account = useAccount();
    const ability = useAbilityContext();
    const emailStatus = useEmailStatus(!!health.data?.isAuthenticated);
    const isEmailServerConfigured = health.data?.hasEmailClient;
    const isDlMobile = useIsDlMobile();

    // ---- DL Fork: embedded-build helpers -------------------------------
    // All three embed useEffects below MUST sit ABOVE any early return
    // in this component (hook-rules). Each effect is double-gated on
    // `IS_EMBEDDED_BUILD && window.parent !== window` so that:
    //   1. The standalone build (MODE=standalone) is byte-equivalent
    //      to the pre-fork behaviour: effects early-return.
    //   2. An embedded build accidentally deployed to a standalone URL
    //      (top-level tab) also behaves standalone.
    const isEmbeddedInIframe = IS_EMBEDDED_BUILD && window.parent !== window;
    const unauthenticated = !!health.data && !health.data.isAuthenticated;
    // `isFullyReady` must include organizationUuid so we don't emit
    // the host-ready message before PrivateRoute would have settled on
    // the final `<>{children}</>` render path.
    const isFullyReady =
        health.data?.isAuthenticated === true &&
        !health.isInitialLoading &&
        !isInitialLoading &&
        !emailStatus.isInitialLoading &&
        !!data?.organizationUuid;

    // Initialize based on whether ability already has rules (e.g., from previous navigation)
    // This prevents a loading flash when navigating between pages
    const [abilityInitialized, setAbilityInitialized] = useState(
        () => ability.rules.length > 0,
    );

    usePromotePendingSsoLogin();

    useEffect(() => {
        if (data) {
            ability.update(data.abilityRules);
            setAbilityInitialized(true);
        }
    }, [ability, data]);

    // ---- DL Fork: mobile body class -------------------------------------
    // When the host app signals mobile (?dl_mobile=1) on the embedded build,
    // tag `document.body` with `dl-mobile`. CSS rules in
    // DLDateFilter.module.css (`:global(.dl-mobile) main { min-width: 0 }`)
    // are descendant selectors so a body-level class still matches every
    // <main> in the SPA. Hoisting here (instead of per-page wrappers) means
    // every current and future page inherits the mobile min-width override
    // automatically.
    //
    // No cleanup function on purpose. Once the class is on body it is
    // correct for the iframe lifetime — removal would only introduce a
    // single-frame race between route trees. Iframe reload (sessionEpoch++)
    // gives a fresh document anyway.
    useEffect(() => {
        if (!isDlMobile) return;
        document.body.classList.add('dl-mobile');
    }, [isDlMobile]);

    // ---- DL Fork: intercept — notify host app before navigating. --------
    // We let useEffect fire the postMessage rather than doing it during
    // render, because postMessage-in-render risks double-fires under
    // React 18 StrictMode / concurrent re-renders.
    useEffect(() => {
        if (!isEmbeddedInIframe) return;
        if (!unauthenticated) return;
        notifyParentSessionExpired();
    }, [unauthenticated, isEmbeddedInIframe]);

    // ---- DL Fork: ready emission ----------------------------------------
    // Fires once per iframe mount when PrivateRoute would actually render
    // `<>{children}</>`. The fire-once guard lives inside `notifyParentReady`,
    // so even if `isFullyReady` flickers we won't re-post.
    useEffect(() => {
        if (!IS_EMBEDDED_BUILD || window.parent === window) return;
        if (!isFullyReady) return;
        notifyParentReady();
    }, [
        isFullyReady,
        health.data?.isAuthenticated,
        health.isInitialLoading,
        isInitialLoading,
        emailStatus.isInitialLoading,
        data?.organizationUuid,
    ]);

    // ---- DL Fork: visibility probe ---------------------------------------
    // When the tab is re-focussed after a long idle, the session cookie
    // may have expired server-side while the SPA is still optimistic
    // client-side. A single `/api/v1/user` fetch confirms; a 401 kicks
    // the host app into a remount.
    //
    // Guards:
    //  - 500 ms mount cooldown: skip spurious visibilitychange events
    //    that fire during initial layout.
    //  - 2 s debounce: alt-tab burst protection.
    //  - Single-flight: no overlapping probes.
    //  - Gated on `health.data?.isAuthenticated`: don't probe before
    //    we've even completed the first auth round-trip.
    const mountTimeRef = useRef(Date.now());
    const lastProbeAtRef = useRef(0);
    const probeInFlightRef = useRef(false);

    useEffect(() => {
        if (!IS_EMBEDDED_BUILD || window.parent === window) return undefined;

        async function probe() {
            if (document.visibilityState !== 'visible') return;
            if (!health.data?.isAuthenticated) return;
            const now = Date.now();
            if (now - mountTimeRef.current < 500) return;
            if (now - lastProbeAtRef.current < 2000) return;
            if (probeInFlightRef.current) return;
            probeInFlightRef.current = true;
            lastProbeAtRef.current = now;
            try {
                const r = await fetch('/api/v1/user', {
                    credentials: 'include',
                });
                if (r.status === 401) {
                    notifyParentSessionExpired();
                }
            } catch {
                // Network error — swallow. A real expiry will be
                // surfaced either by the next query's 401 handler
                // (useQueryError) or by the next probe.
            } finally {
                probeInFlightRef.current = false;
            }
        }

        document.addEventListener('visibilitychange', probe);
        // Fire once on mount in case the tab is already visible AND
        // was restored from bfcache with a stale session. The cooldown
        // guards above still apply.
        void probe();
        return () => {
            document.removeEventListener('visibilitychange', probe);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [health.data?.isAuthenticated]);

    // -------------------------------------------------------------------

    if (health.isInitialLoading || health.error) {
        return <PageSpinner />;
    }

    if (!health.data?.isAuthenticated) {
        // DL Fork: in the embedded build, we must NOT mount LoginLanding —
        // its effect chain fires API calls + a possible SSO redirect
        // that break the host app's session-expired handshake.
        return isEmbeddedInIframe ? (
            <EmbeddedReconnectingGate />
        ) : (
            <Navigate
                to={{
                    pathname: '/login',
                }}
                state={{ from: location }}
            />
        );
    }

    if (
        isInitialLoading ||
        emailStatus.isInitialLoading ||
        (data && !abilityInitialized)
    ) {
        return <PageSpinner />;
    }

    if (isEmailServerConfigured && !emailStatus.data?.isVerified) {
        return (
            <Navigate
                to={{
                    pathname: '/verify-email',
                }}
                state={{ from: location }}
            />
        );
    }

    // The user query only starts once the account comes back saying it is a
    // registered user, and neither query retries — so "no data, no error" also
    // covers a user query that will never run. Holding on that is a spinner
    // with no way out, which is why the hold has to end wherever the chain has
    // stopped progressing: the account errored (a blip on /user/account), or it
    // resolved to someone the user query is never fetched for. Both fall
    // through to the redirect below, as they did before the hold existed.
    const isUserStillResolving =
        !account.isError && (!account.data || account.data.isRegisteredUser());

    if (!data && !isError && isUserStillResolving) {
        return <PageSpinner />;
    }

    if (!data?.organizationUuid) {
        return (
            <Navigate
                to={{
                    pathname: '/join-organization',
                }}
                state={{ from: location }}
            />
        );
    }

    return <>{children}</>;
};

export default PrivateRoute;
